import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { isMobileOnly } from 'react-device-detect';
import Dropzone from './DropzoneArea/Dropzone';
import PdfCanvas from './DropzoneArea/PdfCanvas';
import { ReactComponent as FrontOfId } from '../assets/images/svg/front-of-card.svg';
import Paragraph from './Paragraph/Paragraph';
import themeColors from '../assets/styles/themeColors';
import { variables } from '../assets/styles/variables';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const DragContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: ${variables.borderRadius.borderRadiusMd};
  border: 1px dashed ${themeColors.colorBorderPrimaryDark};
  background: ${themeColors.colorBackgroundDropzone};
  text-align: center;
  padding: 0.75rem;
  cursor: pointer;

  svg {
    max-height: 93px;
    margin-bottom: 1.5rem;
  }
`;

const Image = styled.img`
  object-fit: contain;
  width: 100%;
  height: 100%;
  max-height: 17.5rem;
`;

const ChangeTip = styled.div`
  color: ${themeColors.colorPrimary};
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  white-space: nowrap;
  z-index: 2;
  pointer-events: ${({ $interactive }) => ($interactive ? 'auto' : 'none')};
  cursor: ${({ $interactive }) => ($interactive ? 'pointer' : 'default')};
  text-decoration: underline;
  margin-top: 20px;
`;

const DropzoneText = ({ pdfFormatDisabled }) => {
  const { t } = useTranslation();

  return (
    <>
      <Paragraph mb={12}>
        {isMobileOnly
          ? t('kycVerification.dropHereMobile')
          : t('kycVerification.dropHere')}
      </Paragraph>
      <Paragraph variant="small" color={themeColors.colorTextXLLight}>
        {pdfFormatDisabled
          ? t('kycVerification.supportsWothoutPdf')
          : t('kycVerification.supports')}
      </Paragraph>
    </>
  );
};

const DropzoneArea = ({
  onUpload,
  frontSide,
  pdfFormatDisabled,
  isPassport,
}) => {
  const { t } = useTranslation();
  const [imageUrl, setImageUrl] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);

  const onDrop = useCallback(
    (acceptedFiles) => {
      acceptedFiles.forEach((file) => {
        const reader = new FileReader();
        reader.onload = () => {
          const dataUrl = reader.result;
          if (file.type !== 'application/pdf') {
            setImageUrl(dataUrl);
            setPdfUrl(null);
          } else {
            setImageUrl(null);
            setPdfUrl(dataUrl);
          }
          onUpload(file);
        };
        reader.readAsDataURL(file);
      });
    },
    [onUpload],
  );

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    accept: pdfFormatDisabled
      ? 'image/png, image/jpeg, image/jpg, image/gif'
      : 'image/jpeg, image/png, image/jpg, image/gif, application/pdf, .pdf',
    maxFiles: 1,
    noClick: !!pdfUrl,
  });

  return (
    <Container>
      <DragContainer hasData={pdfUrl || imageUrl} {...getRootProps()}>
        {imageUrl && <Image src={imageUrl} alt="" />}
        {pdfUrl && <PdfCanvas dataUrl={pdfUrl} />}

        {!imageUrl && !pdfUrl && (
          <>
            {frontSide && <FrontOfId />}
            {isPassport && <FrontOfId />}
            <DropzoneText pdfFormatDisabled={pdfFormatDisabled} />
          </>
        )}
        <Dropzone getInputProps={getInputProps} pointerEventsNone={!!pdfUrl} />
      </DragContainer>
      {frontSide && (
        <Paragraph mt={24} fontWeight={600}>
          {t('kycVerification.front')}
        </Paragraph>
      )}
      {(imageUrl || pdfUrl) && (
        <ChangeTip
          $interactive={!!pdfUrl || imageUrl}
          onClick={pdfUrl || imageUrl ? open : undefined}
        >
          {t('kycVerification.tapToChange')}
        </ChangeTip>
      )}
    </Container>
  );
};

DropzoneArea.propTypes = {
  onUpload: PropTypes.func,
  frontSide: PropTypes.bool,
  pdfFormatDisabled: PropTypes.bool,
  isPassport: PropTypes.bool,
};
DropzoneText.propTypes = {
  pdfFormatDisabled: PropTypes.bool,
};

export default DropzoneArea;
